// Color scale configuration for trend and similarity visualization
// Left-most color (cold/blue) to right-most color (hot/red)
export const COLOR_SCALE = [
  // "#5684c2",
  // "#6790cb",
  // "#759fd3",
  // "#89acdc",
  // "#9abbe3",
  // "#9cc4db",
  // "#99ccd1",
  // "#9bd3c0",
  // "#d7de7e",
  // "#ebdb6e",
  // "#f3d862",
  // "#fdca4d",
  // "#f7b52d",
  // "#f19b00",
  // "#f39500",
  // "#f08400",
  // "#ee750f",
  // "#ee6407",
  // "#e95718",
  // "#e54915",
  // "#e2401f",
  // "#d9330c",
  // "#d9350a",
  // "#d2260d",
  // "#b2090d",

  // --- new color from figma design:
  "#8A96FF",
  "#7AEA98",
  "#BDB9C1",
  "#FFC61A",
  "#EE693F",
];

// Neutral color index (yellow-green area)
export const NEUTRAL_INDEX = 2; // '#9bd3c0'

/**
 * Get color from the scale based on normalized value (0-1)
 * @param value - Normalized value between 0 and 1
 * @returns Hex color string
 */
export const getColorFromScale = (value: number): string => {
  // Clamp value between 0 and 1
  const clampedValue = Math.max(0, Math.min(1, value));

  // Map to scale index
  const index = Math.floor(clampedValue * (COLOR_SCALE.length - 1));

  return COLOR_SCALE[index];
};

/**
 * Get color for trend values
 * @param trend - Trend value (can be positive, negative, or zero)
 * @param minTrend - Minimum trend value in dataset
 * @param maxTrend - Maximum trend value in dataset
 * @returns Hex color string
 */
export const getTrendColor = (
  trend: number,
  minTrend: number,
  maxTrend: number
): string => {
  // Handle zero trend - return neutral color
  if (trend === 0 || trend === null || trend === undefined || isNaN(trend)) {
    return `${COLOR_SCALE[NEUTRAL_INDEX]}`;
  }

  // Calculate the absolute range for negative and positive values separately
  const maxAbsNegative = Math.abs(Math.min(0, minTrend));
  const maxAbsPositive = Math.max(0, maxTrend);

  let normalizedValue: number;

  if (trend < 0) {
    // Map negative values: most negative -> index 0, zero -> NEUTRAL_INDEX
    if (maxAbsNegative === 0) {
      normalizedValue = NEUTRAL_INDEX / (COLOR_SCALE.length - 1);
    } else {
      const negativeRatio = Math.abs(trend) / maxAbsNegative;
      normalizedValue =
        (NEUTRAL_INDEX / (COLOR_SCALE.length - 1)) * (1 - negativeRatio);
    }
  } else {
    // Map positive values: smallest positive -> NEUTRAL_INDEX + 1, most positive -> max index
    if (maxAbsPositive === 0) {
      normalizedValue = NEUTRAL_INDEX / (COLOR_SCALE.length - 1);
    } else {
      const positiveRatio = trend / maxAbsPositive;
      // Ensure positive trends start at least at NEUTRAL_INDEX + 1
      const minPositiveNormalized =
        (NEUTRAL_INDEX + 1) / (COLOR_SCALE.length - 1);
      const maxNormalized = 1.0;
      normalizedValue =
        minPositiveNormalized +
        (maxNormalized - minPositiveNormalized) * positiveRatio;
    }
  }

  const color = getColorFromScale(normalizedValue);
  return `${color}`;
};

/**
 * Get color for similarity values (alternative approach)
 * @param similarity - Similarity score
 * @param minSimilarity - Minimum similarity in dataset
 * @param maxSimilarity - Maximum similarity in dataset
 * @returns Hex color string
 */
export const getSimilarityColor = (
  similarity: number,
  minSimilarity: number,
  maxSimilarity: number
): string => {
  const range = maxSimilarity - minSimilarity;

  if (range === 0) {
    return `${COLOR_SCALE[NEUTRAL_INDEX]}`;
  }

  // Anchor keywords always get the hottest color
  if (similarity === maxSimilarity) {
    return `${COLOR_SCALE[COLOR_SCALE.length - 1]}`;
  }

  // Map similarity values to only the 4 colors used by the legend (indices 0, 2, 3, 4),
  // deliberately skipping index 1 (#7AEA98 green) which is not a similarity color.
  const similarityColors = COLOR_SCALE.filter(c => c !== '#7AEA98');
  const normalizedValue = (similarity - minSimilarity) / range;
  const clampedValue = Math.max(0, Math.min(1, normalizedValue));
  const index = Math.min(
    Math.floor(clampedValue * similarityColors.length),
    similarityColors.length - 1
  );
  return similarityColors[index];
};

/**
 * Get legend colors for trend mode
 * @returns Array of legend items with colors and labels
 */
export const trendLegendItems = [
  {
    color: COLOR_SCALE[0],
    label: "Strong <br/> Negative Trend",
  },
  {
    color: COLOR_SCALE[1],
    label: "Weak <br/> Negative Trend",
  },
  {
    color: COLOR_SCALE[NEUTRAL_INDEX],
    label: "Neutral / <br/> No Trend",
  },
  {
    color: COLOR_SCALE[3],
    label: "Weak <br/> Positive Trend",
  },
  {
    color: COLOR_SCALE[4],
    label: "Strong <br/> Positive Trend",
  },
];

/**
 * Get legend colors for similarity mode
 * @returns Array of legend items with colors and labels
 */
export const similarityLegendItems = [
  {
    color: COLOR_SCALE[0],
    label: "Lowest Similarity",
  },
  {
    color: COLOR_SCALE[2],
    label: "Medium Similarity",
  },
  {
    color: COLOR_SCALE[3],
    label: "High Similarity",
  },
  {
    color: COLOR_SCALE[COLOR_SCALE.length - 1],
    label: "Highest Similarity",
  },
];
